import { DivTooltip, TooltipBody } from '@lst/mk-react-uikit';
import classnames from 'classnames';
import equal from 'fast-deep-equal/react';
import get from 'lodash/get';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Col, Container, Row } from '~/Components/Grid/Grid';
import { State } from '~/Reducers';

import styles from './StatisticsHeader.scss';

const rankIconsSelector = (state: State) => state.ReducerApp.rankBattlesIconsMap;
const ratingIconsSelector = (state: State) => state.ReducerApp.ratingBattlesIconsMap;

export interface StatisticsHeaderItem {
  value: string | number;
  label: string;
  league?: string;
}

export interface StatisticsHeader_Props {
  data: Array<StatisticsHeaderItem>;
  withBestRank?: boolean;
  withBestRating?: boolean;
}

const StatisticsHeader = (props: StatisticsHeader_Props) => {
  const { t } = useTranslation();
  const rankIconsMap = useSelector<State, LeaguesRanksMap>(rankIconsSelector, equal);
  const ratingIconsMap = useSelector<State, LeaguesRatingsMap>(ratingIconsSelector, equal);

  if (!props.data || props.data.length === 0) {
    return (
      <Container>
        <Row>
          <Col>{'NO DATA'}</Col>
        </Row>
      </Container>
    );
  }

  const renderBestRankTooltip = () => {
    return <TooltipBody>{t('Лучший ранг и лига за выбранный период Ранговых боёв')}</TooltipBody>;
  };
  const renderBestRatingTooltip = () => {
    return <TooltipBody>{t('Лучший рейтинг и лига за выбранный период Рейтинговых боёв')}</TooltipBody>;
  };

  const getBestRankItem = useMemo(
    () => (item: StatisticsHeaderItem) => {
      if (item.league) {
        const rankIconUrl = get(rankIconsMap, `${item.league}[${String(item.value)}].default`, undefined);

        return <div className={styles.bestRankItemImage} style={{ backgroundImage: `url(${rankIconUrl})` }} />;
      }
      return null;
    },
    [rankIconsMap],
  );

  const getBestRatingItem = useMemo(
    () => (item: any) => {
      if (item) {
        const splittedRating = String(item.value).split('');
        const ratingNumbersMap = get(ratingIconsMap, `${item.league}.['numbers']`, undefined);
        const ratingEmblemUrl = get(ratingIconsMap, `${item.league}.['emblem']`, undefined);

        return (
          <div>
            <div className={styles.bestRatingItemImage} style={{ backgroundImage: `url(${ratingEmblemUrl})` }}>
              <div className={styles.ratingNumbersContainer}>
                {splittedRating.map((number) => {
                  const ratingNumberUrl = ratingNumbersMap[String(number)];

                  return <div className={styles.ratingNumber} style={{ backgroundImage: `url(${ratingNumberUrl})` }} />;
                })}
              </div>
            </div>
          </div>
        );
      }
    },
    [ratingIconsMap],
  );

  const containersRowStyles = classnames(styles.row, {
    [styles.bestRating]: !!props.withBestRating,
  });

  return (
    <Container>
      <Row className={containersRowStyles}>
        {props.data.map((item: StatisticsHeaderItem, index: number) => {
          let bestItem;
          let bestItemTooltip;
          if (index === props.data.length - 1 && (props.withBestRank || props.withBestRating)) {
            if (props.withBestRank) {
              bestItem = getBestRankItem(item);
              bestItemTooltip = renderBestRankTooltip();
            } else {
              bestItem = getBestRatingItem(item);
              bestItemTooltip = renderBestRatingTooltip();
            }
            return (
              <Col className={styles.cell} key={`stat-item-${item.value}-${index}`}>
                <DivTooltip className={styles.bestRankItem} tooltipBody={bestItemTooltip}>
                  {bestItem}
                  <div className={styles.label}>{item.label}</div>
                </DivTooltip>
              </Col>
            );
          }

          return (
            <Col className={styles.cell} key={`stat-item-${item.value}-${index}`}>
              <div className={styles.title}>{item.value}</div>
              <div className={styles.label}>{item.label}</div>
            </Col>
          );
        })}
      </Row>
    </Container>
  );
};

export default memo(StatisticsHeader);
